import axiosInstance from './axiosConfig';
import type { LpGenerationMode, LpProject } from './lpGenerationService';

export const LP_STAGES = [
  'brief',
  'strategy',
  'outline',
  'copy',
  'design',
  'review',
] as const;
export type LpStage = (typeof LP_STAGES)[number];
export type LpGenerationStage =
  | LpStage
  | 'image'
  | 'concept'
  | 'concepts'
  | 'design_images'
  | 'design_code';
export type LpObjective =
  | 'purchase'
  | 'inquiry'
  | 'download'
  | 'booking'
  | 'registration';
export const LP_STAGE_LABELS: Record<LpStage, string> = {
  brief: '商品理解',
  strategy: '訴求',
  outline: '構成',
  copy: 'コピー',
  design: 'デザイン',
  review: '最終確認',
};
export interface LpBrief {
  product_summary: string;
  target: string;
  objective: LpObjective;
  benefits: string;
  evidence: string;
  offer: string;
  objections: string;
  ad_message: string;
  brand_direction: string;
  missing_information: string;
  cta_label: string;
  cta_url: string;
}
export interface LpStrategyOption {
  id: string;
  name: string;
  promise: string;
  audience: string;
  reason: string;
  evidence: string;
  objection: string;
}
export interface LpStrategy {
  options: LpStrategyOption[];
  selected_id: string | null;
}
export interface LpOutlineSection {
  id: string;
  type: string;
  title: string;
  purpose: string;
  key_points: string;
  evidence: string;
}
export interface LpOutline {
  sections: LpOutlineSection[];
}
export interface LpCopySection {
  id: string;
  title: string;
  content: string;
  cta_label: string;
  notes: string;
  evidence: string;
}
export interface LpCopy {
  sections: LpCopySection[];
  headlines: string[];
}
export interface LpDesignOption {
  id: string;
  name: string;
  reason: string;
  layout: 'split' | 'editorial' | 'centered';
  primary: string;
  accent: string;
  base: string;
  image_direction: string;
  typography?: 'sans' | 'serif' | 'rounded';
  surface?: 'clean' | 'contrast' | 'framed';
  sections?: {
    id: string;
    composition: 'split' | 'reverse' | 'wide' | 'inset' | 'poster';
    artwork_ratio?: '2:3' | '1:1' | '3:2' | '2:1' | '3:1';
    image_prompt: string;
    reference_indices: number[];
  }[];
}
export interface LpDesign {
  options: LpDesignOption[];
  selected_id: string | null;
}
export interface LpReviewIssue {
  severity: 'error' | 'warning';
  message: string;
  section_id: string | null;
}
export interface LpReview {
  issues: LpReviewIssue[];
  checks?: Partial<Record<'facts' | 'desktop' | 'mobile' | 'cta', boolean>>;
}
export interface LpWorkflowStages {
  brief: LpBrief;
  strategy?: LpStrategy;
  outline?: LpOutline;
  copy?: LpCopy;
  design?: LpDesign;
  review?: LpReview;
}
export interface LpAsset {
  s3_key: string;
  url: string;
  source: string;
  fingerprint: string;
  stale: boolean;
}
export interface LpWorkflow extends LpWorkflowStages {
  version: number;
  revision: number;
  confirmed: Record<LpStage, boolean>;
  assets: Record<string, LpAsset>;
  operation: null | {
    id: string;
    stage: LpGenerationStage;
    section_id: string | null;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    error: string | null;
    started_at: string;
    task_index?: number;
    tasks?: {
      kind: 'concept' | 'image' | 'code';
      design_id: string;
      section_id: string | null;
      status: 'pending' | 'processing' | 'completed' | 'failed';
    }[];
  };
}
export interface LpWorkflowProject extends LpProject {
  workflow: LpWorkflow;
  review_blockers: LpReviewIssue[];
  image_credit_estimate: number;
  concept_credit_estimate?: number;
  generation_models?: {
    planning: string | null;
    coding: string | null;
    image: string;
  };
  design_candidates?: Record<
    string,
    { concept: LpAsset | null; images_ready: number; code_ready: boolean }
  >;
  reference_images: { s3_key: string; category: string; url: string }[];
}
export interface CreateLpWorkflow {
  title?: string;
  product_id?: number;
  product_info: string;
  generation_mode: LpGenerationMode;
  objective: LpObjective;
  cta_label: string;
  cta_url: string;
  selection?: {
    segment_id: string;
    segment_name: string;
    appeal_id: string;
    appeal_name: string;
    appeal_direction: string;
    additional_context: string;
  };
  reference_images: {
    s3_key?: string;
    image_data?: string;
    category: string;
  }[];
}
const base = '/lp/workflow/projects';
const unwrap = (response: {
  data: { data: LpWorkflowProject };
}): LpWorkflowProject => response.data.data;
const lpWorkflowService = {
  create: async (data: CreateLpWorkflow) =>
    unwrap(await axiosInstance.post(base, data)),
  get: async (id: string) => unwrap(await axiosInstance.get(`${base}/${id}`)),
  save: async (
    id: string,
    revision: number,
    stage: LpStage,
    data: NonNullable<LpWorkflowStages[LpStage]>,
    confirm: boolean,
  ) =>
    unwrap(
      await axiosInstance.put(`${base}/${id}/stages/${stage}`, {
        revision,
        data,
        confirm,
      }),
    ),
  generate: async (
    id: string,
    revision: number,
    stage: LpGenerationStage,
    instruction = '',
    section_id?: string,
    design_id?: string,
  ) =>
    unwrap(
      await axiosInstance.post(`${base}/${id}/generate/${stage}`, {
        revision,
        instruction,
        section_id,
        design_id,
      }),
    ),
  attach: async (
    id: string,
    revision: number,
    sectionId: string,
    s3_key: string | null,
  ) =>
    unwrap(
      await axiosInstance.put(`${base}/${id}/assets/${sectionId}`, {
        revision,
        s3_key,
      }),
    ),
  preview: async (
    id: string,
    candidate?: string,
    heroOnly = false,
    download = false,
  ): Promise<string> => {
    const response = await axiosInstance.get(`${base}/${id}/preview`, {
      params: {
        candidate,
        hero_only: heroOnly ? 1 : 0,
        export: download ? 1 : 0,
      },
    });
    return response.data.data.html;
  },
  error: (error: unknown): string => {
    const e = error as {
      response?: {
        data?: { message?: string; errors?: Record<string, string[]> };
      };
    };
    const body = e.response?.data;
    return body?.errors
      ? Object.values(body.errors).flat().join(' ')
      : body?.message || '処理に失敗しました。もう一度お試しください。';
  },
};
export default lpWorkflowService;
